<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <v-card class="card-container pa-6" elevation="0">
        <v-card-title class="px-0">
          <div class="d-flex align-center">
            <v-btn
              icon
              @click="handleCancel"
              class="mr-3"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="title-text">
              {{ isEditMode ? "แก้ไขสินค้า" : "เพิ่มสินค้าใหม่" }}
            </span>
          </div>
        </v-card-title>

        <v-divider class="my-4"></v-divider>

        <!-- แจ้งเตือนสินค้าต้องห้ามและสินค้าควบคุม -->
        <div v-if="prohibitedPolicy" class="prohibited-notice">
          <div class="prohibited-notice__header" @click="noticeExpanded = !noticeExpanded">
            <div class="d-flex align-center gap-2">
              <v-icon color="#b91c1c" size="20">mdi-alert-circle</v-icon>
              <span class="prohibited-notice__title">{{ prohibitedPolicy.title }}</span>
              <span class="prohibited-notice__subtitle ml-1">(โปรดอ่านก่อนลงขาย)</span>
            </div>
            <v-icon size="20" color="#6b7280" class="prohibited-notice__chevron" :class="{ expanded: noticeExpanded }">
              mdi-chevron-down
            </v-icon>
          </div>

          <div v-show="noticeExpanded" class="prohibited-notice__body">
            <pre class="prohibited-content">{{ prohibitedPolicy.content }}</pre>
          </div>
        </div>

        <v-card-text class="px-0">
          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="form-content-wrapper">
              <v-row class="justify-center">
                <!-- รูปภาพสินค้า -->
                <v-col cols="12" class="d-flex justify-center">
                  <div class="images-section-container">
                    <span class="detail-text text-center d-block mb-4">
                      <span class="req">*</span>รูปภาพสินค้า
                    </span>
                    <div class="images-upload-section">
                      <div class="images-grid">
                        <div
                          v-for="(image, index) in images"
                          :key="index"
                          class="image-item-wrapper"
                          :class="{ 'dragging': draggedIndex === index, 'drag-over': dragOverIndex === index }"
                          :draggable="images.length > 1"
                          @dragstart="handleDragStart($event, index)"
                          @dragend="handleDragEnd"
                          @dragover.prevent="handleDragOver($event, index)"
                          @dragenter.prevent="handleDragEnter(index)"
                          @dragleave="handleDragLeave"
                          @drop.prevent="handleDrop($event, index)"
                        >
                          <div class="image-preview-wrapper">
                            <v-img
                              :src="image.preview || getImageUrl(image.url)"
                              class="image-preview"
                              contain
                            />
                            <div class="image-actions">
                              <v-btn
                                icon
                                x-small
                                class="remove-image-btn"
                                @click.stop="removeImage(index)"
                              >
                                <v-icon color="white" size="18">mdi-close</v-icon>
                              </v-btn>
                              <div class="image-order-badge" v-if="images.length > 1">
                                {{ index + 1 }}
                              </div>
                            </div>
                            <div class="image-drag-handle" v-if="images.length > 1">
                              <v-icon color="white" size="16">mdi-drag</v-icon>
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="images.length < 10"
                          class="image-upload-placeholder"
                          @click="triggerFileInput"
                        >
                          <v-icon size="48" color="#9ca3af">mdi-image-plus-outline</v-icon>
                          <span class="upload-text">เพิ่มรูปภาพ</span>
                          <span class="upload-hint">(สูงสุด 10 รูป)</span>
                        </div>
                      </div>
                      <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        multiple
                        style="display: none"
                        @change="handleImageChange"
                      />
                      <div class="image-upload-info mt-3 text-center">
                        <span class="info-text">
                          <v-icon small color="#6b717f" class="mr-1">mdi-information-outline</v-icon>
                          รูปภาพหลัก: รูปแรกจะเป็นรูปหลักของสินค้า
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- ข้อมูลสินค้า -->
                <v-col cols="12" class="d-flex justify-center">
                  <div class="product-info-container">
                    <v-row>
                      <!-- ชื่อสินค้า -->
                      <v-col cols="12">
                        <span class="detail-text">
                          <span class="req">*</span>ชื่อสินค้า
                        </span>
                        <v-text-field
                          v-model.trim="form.name"
                          placeholder="ระบุชื่อสินค้า"
                          v-bind="tfProps"
                          :rules="[required]"
                        />
                      </v-col>

                      <!-- SKU -->
                      <v-col cols="12" md="6">
                        <span class="detail-text">
                          <span class="req">*</span>SKU
                        </span>
                        <v-text-field
                          v-model.trim="form.sku"
                          placeholder="ระบุ SKU"
                          v-bind="tfProps"
                          :rules="[required]"
                        />
                      </v-col>

                      <!-- ประเภทสินค้า -->
                      <v-col cols="12" md="6">
                        <span class="detail-text">
                          <span class="req">*</span>ประเภทสินค้า
                        </span>
                        <v-select
                          v-model="form.category"
                          :items="categories"
                          item-text="name"
                          item-value="id"
                          placeholder="เลือกประเภทสินค้า"
                          v-bind="selProps"
                          :rules="[required]"
                          :loading="loadingCategories"
                        />
                      </v-col>

                      <!-- สถานะ -->
                      <v-col cols="12" md="6">
                        <span class="detail-text">
                          <span class="req">*</span>สถานะ
                        </span>
                        <v-select
                          v-model="form.status"
                          :items="statusOptions"
                          item-text="label"
                          item-value="value"
                          placeholder="เลือกสถานะ"
                          v-bind="selProps"
                          :rules="[required]"
                        />
                      </v-col>

                      <!-- คำอธิบาย -->
                      <v-col cols="12">
                        <span class="detail-text">คำอธิบายสินค้า</span>
                        <v-textarea
                          v-model.trim="form.description"
                          placeholder="ระบุคำอธิบายสินค้า (ถ้ามี)"
                          v-bind="taProps"
                          rows="4"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Variants/Options Section -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-card class="variant-card" elevation="0">
                  <v-card-title class="px-0">
                    <div class="d-flex align-center justify-space-between w-100">
                      <span class="detail-text mb-0">
                        ราคาและสต็อกสินค้า
                      </span>
                      <v-switch
                        v-model="form.hasVariants"
                        label="สินค้านี้มีตัวเลือก"
                        hide-details
                        dense
                        color="#F4811F"
                      ></v-switch>
                    </div>
                  </v-card-title>

                  <v-card-text class="px-0">
                    <!-- ราคาและสต็อก (แสดงเมื่อไม่มี variants) -->
                    <div v-if="!form.hasVariants" class="price-stock-section">
                      <v-row>
                        <v-col cols="12" md="6">
                          <span class="detail-text">
                            <span class="req">*</span>ราคา (฿)
                          </span>
                          <v-text-field
                            v-model.number="form.price"
                            placeholder="0.00"
                            type="number"
                            min="0"
                            step="0.01"
                            v-bind="tfProps"
                            :rules="[required, priceRule]"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <span class="detail-text">
                            <span class="req">*</span>จำนวนสต็อก
                          </span>
                          <v-text-field
                            v-model.number="form.stock"
                            placeholder="0"
                            type="number"
                            min="0"
                            v-bind="tfProps"
                            :rules="[required, stockRule]"
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Variants (แสดงเมื่อมี variants) -->
                    <div v-if="form.hasVariants" class="variants-section">
                      <div
                        v-for="(variant, variantIndex) in form.variants"
                        :key="variantIndex"
                        class="variant-item mb-4"
                      >
                        <v-card class="variant-card-item" elevation="1">
                          <v-card-title class="variant-header">
                            <span class="variant-title">ตัวเลือก {{ variantIndex + 1 }}</span>
                            <v-btn
                              icon
                              x-small
                              @click="removeVariant(variantIndex)"
                              v-if="form.variants.length > 1"
                            >
                              <v-icon color="#EF4444">mdi-delete-outline</v-icon>
                            </v-btn>
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12" md="4">
                                <span class="detail-text">ชื่อตัวเลือก</span>
                                <v-text-field
                                  v-model.trim="variant.color"
                                  placeholder="เช่น สี, ขนาด"
                                  v-bind="tfProps"
                                  :rules="[required]"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <span class="detail-text">ราคา (฿)</span>
                                <v-text-field
                                  v-model.number="variant.price"
                                  placeholder="0.00"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  v-bind="tfProps"
                                />
                              </v-col>
                              <v-col cols="12" md="4">
                                <span class="detail-text">จำนวนสต็อก</span>
                                <v-text-field
                                  v-model.number="variant.stock"
                                  placeholder="0"
                                  type="number"
                                  min="0"
                                  v-bind="tfProps"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>

                      <v-btn
                        outlined
                        color="#F4811F"
                        @click="addVariant"
                        class="mt-2"
                        :disabled="form.variants.length >= 10"
                      >
                        <v-icon left>mdi-plus</v-icon>
                        เพิ่มตัวเลือก
                      </v-btn>
                      <div v-if="form.variants.length >= 10" class="mt-2">
                        <span class="info-text">เพิ่มตัวเลือกได้สูงสุด 10 ตัวเลือก</span>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="my-4"></v-divider>

        <v-card-actions class="px-0">
          <v-spacer></v-spacer>
          <v-btn
            outlined
            color="#6B717F"
            class="mr-3"
            @click="handleCancel"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="#F4811F"
            dark
            :loading="saving"
            @click="handleSave"
          >
            {{ isEditMode ? "บันทึกการแก้ไข" : "เพิ่มสินค้า" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import {
  createSellerProductApi,
  getProductCategoriesApi,
  getSellerProductByIdApi,
  updateSellerProductApi,
} from "@/services/api/sellerServices";
import { getPolicyByTypeApi } from "@/services/api/policy";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "SellerProductForm",

  data() {
    return {
      isLoading: false,
      saving: false,
      valid: false,
      noticeExpanded: false,
      prohibitedPolicy: null,
      isEditMode: false,
      productId: null,
      images: [], // Array of { file, preview, url }
      loadingCategories: false,
      categories: [],
      draggedIndex: null,
      dragOverIndex: null,
      form: {
        name: "",
        sku: "",
        category: null,
        price: null,
        stock: null,
        status: "draft",
        description: "",
        hasVariants: false,
        variants: [
          {
            name: "",
            price: null,
            stock: null,
          },
        ],
      },
      statusOptions: [
        { label: "เปิดใช้งาน", value: "active" },
        { label: "ปิดใช้งาน", value: "inactive" },
        { label: "แบบร่าง", value: "draft" },
      ],
      tfProps: {
        outlined: true,
        hideDetails: "auto",
        dense: true,
      },
      taProps: {
        outlined: true,
        hideDetails: "auto",
        dense: true,
        autoGrow: true,
      },
      selProps: {
        outlined: true,
        hideDetails: "auto",
        dense: true,
        menuProps: { offsetY: true },
      },
    };
  },

  computed: {
    required() {
      return (v) => !!v || "กรุณากรอกข้อมูล";
    },
    priceRule() {
      return (v) => {
        if (v === null || v === undefined || v === "") return "กรุณากรอกราคา";
        if (isNaN(v) || Number(v) < 0) return "ราคาต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0";
        return true;
      };
    },
    stockRule() {
      return (v) => {
        if (v === null || v === undefined || v === "") return "กรุณากรอกจำนวนสต็อก";
        if (isNaN(v) || Number(v) < 0 || !Number.isInteger(Number(v))) {
          return "จำนวนสต็อกต้องเป็นจำนวนเต็มที่มากกว่าหรือเท่ากับ 0";
        }
        return true;
      };
    },
  },

  created() {
    this.checkEditMode();
    this.loadCategories();
    this.loadProhibitedPolicy();
    if (this.isEditMode) {
      this.loadProduct();
    }
  },

  methods: {
    checkEditMode() {
      this.productId = this.$route.params.id;
      this.isEditMode = !!this.productId;
    },

    async loadProhibitedPolicy() {
      try {
        const res = await getPolicyByTypeApi("prohibited_products");
        this.prohibitedPolicy = res?.data?.data || null;
      } catch {
        this.prohibitedPolicy = null;
      }
    },

    async loadCategories() {
      this.loadingCategories = true;
      try {
        const res = await getProductCategoriesApi();
        console.log("Categories:", res);
        
        this.categories = res?.data;
      } catch (err) {
        console.error("Error loading categories:", err);
        swalAlert.Error(
          "โหลดประเภทสินค้าไม่สำเร็จ",
          err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลประเภทสินค้าได้"
        );
        this.categories = [];
      } finally {
        this.loadingCategories = false;
      }
    },

    async loadProduct() {
      if (!this.productId) return;

      this.isLoading = true;
      try {
        const res = await getSellerProductByIdApi(this.productId);
        const product = res?.data || res?.product || null;
        if (product) {
          this.form = {
            name: product.productName || product.name || "",
            sku: product.modelCode || product.sku || "",
            category: product.category?.id || product.category || null,
            price: product.price || null,
            stock: product.stock || null,
            status: product.status || "draft",
            description: product.description || "",
            hasVariants: (product.variants && product.variants.length > 0) || false,
            variants: product.variants && product.variants.length > 0
              ? product.variants.map((v) => ({
                  color: v.color || "",
                  price: v.price || null,
                  stock: v.stock || null,
                }))
              : [
                  {
                    color: "",
                    price: null,
                    stock: null,
                  },
                ],
          };

          // Load images
          if (product.images && product.images.length > 0) {
            // เรียงตาม seq และให้ isMain อยู่ลำดับแรก
            const sortedImages = [...product.images].sort((a, b) => {
              // ให้ isMain อยู่แรกสุด
              if (a.isMain && !b.isMain) return -1;
              if (!a.isMain && b.isMain) return 1;
              // แล้วเรียงตาม seq
              return (a.seq || 0) - (b.seq || 0);
            });
            
            this.images = sortedImages.map((img) => ({
              url: img.pathImage || img.image || img,
              preview: null,
              file: null,
            }));
          } else if (product.image) {
            // Fallback สำหรับ legacy image field
            this.images = [
              {
                url: product.image,
                preview: null,
                file: null,
              },
            ];
          } else {
            this.images = [];
          }
        }
 



      } catch (err) {
        console.error("Error loading product:", err);
        swalAlert.Error(
          "โหลดข้อมูลไม่สำเร็จ",
          err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลสินค้าได้"
        );
        this.$router.push({ name: "seller_products" });
      } finally {
        this.isLoading = false;
      }
    },

    getImageUrl(image) {
      if (!image) return "";
      if (/^https?:\/\//i.test(image) || image.startsWith("blob:")) {
        return image;
      }
      const base = process.env.VUE_APP_API_URL || "";
      if (!base) return image;
      const cleanBase = base.replace(/\/+$/, "");
      const cleanPath = image.startsWith("/") ? image : `/${image}`;
      return `${cleanBase}${cleanPath}`;
    },

    triggerFileInput() {
      this.$refs.fileInput?.click();
    },

    handleImageChange(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      const remainingSlots = 10 - this.images.length;
      if (files.length > remainingSlots) {
        swalAlert.Error(`สามารถเพิ่มรูปภาพได้อีก ${remainingSlots} รูปเท่านั้น`);
        return;
      }

      files.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          swalAlert.Error("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          swalAlert.Error("ขนาดไฟล์ต้องไม่เกิน 5 MB");
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push({
            file: file,
            preview: e.target.result,
            url: null,
          });
        };
        reader.readAsDataURL(file);
      });

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    removeImage(index) {
      this.images.splice(index, 1);
    },

    handleDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/html", event.target.outerHTML);
      event.target.style.opacity = "0.5";
    },

    handleDragEnd(event) {
      event.target.style.opacity = "1";
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },

    handleDragOver(event, index) {
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      event.dataTransfer.dropEffect = "move";
    },

    handleDragEnter(index) {
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      this.dragOverIndex = index;
    },

    handleDragLeave(event) {
      // Check if we're actually leaving the element (not just entering a child)
      const relatedTarget = event.relatedTarget;
      if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
        this.dragOverIndex = null;
      }
    },

    handleDrop(event, dropIndex) {
      if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
        this.dragOverIndex = null;
        return;
      }

      const draggedItem = this.images[this.draggedIndex];
      
      // Remove the dragged item
      this.images.splice(this.draggedIndex, 1);
      
      // Insert at new position
      const newIndex = this.draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      this.images.splice(newIndex, 0, draggedItem);

      this.draggedIndex = null;
      this.dragOverIndex = null;
    },

    addVariant() {
      if (this.form.variants.length >= 10) {
        swalAlert.Error("เพิ่มตัวเลือกได้สูงสุด 10 ตัวเลือก");
        return;
      }
      this.form.variants.push({
        name: "",
        price: null,
        stock: null,
      });
    },

    removeVariant(index) {
      if (this.form.variants.length <= 1) {
        swalAlert.Error("ต้องมีอย่างน้อย 1 ตัวเลือก");
        return;
      }
      this.form.variants.splice(index, 1);
    },

    async handleSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const res = await swalAlert.Dialog(
        this.isEditMode ? "ยืนยันการแก้ไขสินค้า" : "ยืนยันการเพิ่มสินค้า",
        this.isEditMode
          ? "คุณต้องการบันทึกการแก้ไขสินค้านี้หรือไม่?"
          : "คุณต้องการเพิ่มสินค้านี้หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "ยืนยัน",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      this.saving = true;

      try {
        // Calculate price and stock from variants if has variants
        let finalPrice = this.form.price || 0;
        let finalStock = this.form.stock || 0;

        if (this.form.hasVariants && this.form.variants.length > 0) {
          const validVariants = this.form.variants.filter(
            (v) => v.color && v.color.trim() !== "" && v.price !== null && v.price !== undefined && v.price !== ""
          );

          if (validVariants.length > 0) {
            // Find minimum price
            const prices = validVariants
              .map((v) => Number(v.price))
              .filter((p) => !isNaN(p) && p >= 0);
            if (prices.length > 0) {
              finalPrice = Math.min(...prices);
            } else {
              finalPrice = 0;
            }

            // Find maximum stock
            const stocks = validVariants
              .map((v) => Number(v.stock))
              .filter((s) => !isNaN(s) && s >= 0);
            if (stocks.length > 0) {
              finalStock = Math.max(...stocks);
            } else {
              finalStock = 0;
            }
          } else {
            // If no valid variants, set default values
            finalPrice = 0;
            finalStock = 0;
          }
        }

        // Ensure values are not null
        finalPrice = finalPrice !== null && finalPrice !== undefined ? finalPrice : 0;
        finalStock = finalStock !== null && finalStock !== undefined ? finalStock : 0;

        const formData = new FormData();
        formData.append("name", this.form.name);
        formData.append("sku", this.form.sku);
        if (this.form.category) {
          formData.append("category", this.form.category);
        }
        formData.append("price", finalPrice);
        formData.append("stock", finalStock);
        formData.append("status", this.form.status);
        if (this.form.description) {
          formData.append("description", this.form.description);
        }

        // Append images
        this.images.forEach((image) => {
          if (image.file) {
            formData.append("images", image.file);
          } else if (image.url) {
            formData.append("existingImages", image.url);
          }
        });

        // Append variants if hasVariants is true
        if (this.form.hasVariants && this.form.variants.length > 0) {
          const validVariants = this.form.variants.filter(
            (v) => v.color && v.color.trim() !== ""
          );
          if (validVariants.length > 0) {
            formData.append("variants", JSON.stringify(validVariants));
          }
        }

        if (this.isEditMode) {
          await updateSellerProductApi(this.productId, formData);
          console.log("Updating product:", this.productId, formData);
        } else {
            console.log("Creating product:", formData);
            // return;
          await createSellerProductApi(formData);
        }

        await swalAlert.Success(
          this.isEditMode ? "แก้ไขสินค้าสำเร็จ" : "เพิ่มสินค้าสำเร็จ"
        );
        this.$router.push({ name: "seller_products" });
      } catch (err) {
        swalAlert.Error(
          this.isEditMode ? "แก้ไขสินค้าไม่สำเร็จ" : "เพิ่มสินค้าไม่สำเร็จ",
          err?.response?.data?.message ||
            (this.isEditMode
              ? "ไม่สามารถแก้ไขสินค้าได้"
              : "ไม่สามารถเพิ่มสินค้าได้")
        );
      } finally {
        this.saving = false;
      }
    },

    handleCancel() {
      this.$router.push({ name: "seller_products" });
    },
  },
};
</script>

<style scoped>
.prohibited-notice {
  margin: 0 0 20px 0;
  border: 1px solid #fecaca;
  border-radius: 10px;
  overflow: hidden;
  background: #fff5f5;
}
.prohibited-notice__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  gap: 8px;
}
.prohibited-notice__header:hover {
  background: #fee2e2;
}
.prohibited-notice__title {
  font-size: 14px;
  font-weight: 700;
  color: #b91c1c;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}
.prohibited-notice__subtitle {
  font-size: 12px;
  color: #ef4444;
  font-style: italic;
}
.prohibited-notice__chevron {
  transition: transform 0.25s;
  flex-shrink: 0;
}
.prohibited-notice__chevron.expanded {
  transform: rotate(180deg);
}
.prohibited-notice__body {
  padding: 0 16px 16px;
  border-top: 1px solid #fecaca;
}
.prohibited-content {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  color: #374151;
  line-height: 1.9;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
  margin: 0;
  padding: 4px 0;
}

.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}

.detail-text {
  font-size: 14px;
  font-weight: 500;
  color: #394253;
  display: block;
  margin-bottom: 8px;
}

.req {
  color: #ef4444;
  margin-right: 4px;
}

.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}

.form-content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.images-section-container {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fafbfc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.product-info-container {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 0;
}

.images-upload-section {
  width: 100%;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 20px;
  max-width: 100%;
  justify-items: center;
}

.image-item-wrapper {
  position: relative;
  width: 100%;
  max-width: 160px;
  transition: transform 0.2s, opacity 0.2s;
  cursor: move;
}

.image-item-wrapper.dragging {
  opacity: 0.5;
  transform: scale(0.95);
  z-index: 1000;
}

.image-item-wrapper.drag-over {
  transform: scale(1.05);
  border: 2px dashed #F4811F;
  border-radius: 12px;
}

.image-item-wrapper:not(.dragging):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-preview-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  cursor: move;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 8px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.4), transparent);
  opacity: 0;
  transition: opacity 0.3s;
}

.image-preview-wrapper:hover .image-actions {
  opacity: 1;
}

.remove-image-btn {
  background-color: rgba(239, 68, 68, 0.9) !important;
}

.image-order-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.image-drag-handle {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 4px;
  cursor: move;
}

.image-upload-placeholder {
  aspect-ratio: 1;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f9fafb;
  min-height: 160px;
  width: 100%;
  max-width: 160px;
}

.image-upload-placeholder:hover {
  border-color: #F4811F;
  background-color: #fef2f2;
}

.upload-text {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6b717f;
}

.upload-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.image-upload-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background-color: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #bae6fd;
}

.info-text {
  font-size: 13px;
  color: #0369a1;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.variant-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
}

.price-stock-section {
  padding: 8px 0;
}

.variant-card-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.variant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.variant-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2a39;
}
</style>
